import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ProgrammeModel, ProgrammeCohortModel } from '@/models/programme.model'
import { programmesService } from '@/services/programmesService'
import { admissionsService } from '@/services/admissionsService'
import { toast } from 'react-toastify'

interface Props {
  isOpen: boolean
  onClose: () => void
  applicationId: string
  currentProgrammeId?: string | null
  currentProgrammeCohortId?: string | null
}

export const ApplicationProgrammeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  applicationId,
  currentProgrammeId,
  currentProgrammeCohortId,
}) => {
  const queryClient = useQueryClient()
  const [programmeId, setProgrammeId] = useState<string>('')
  const [programmeCohortId, setProgrammeCohortId] = useState<string>('')

  const programmesQuery = useQuery<ProgrammeModel[]>({
    queryKey: ['programmes', 'with-cohorts'],
    queryFn: () => programmesService.getProgrammes({ requireAuth: true }),
    enabled: isOpen,
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    if (isOpen) {
      setProgrammeId(currentProgrammeId ?? '')
      setProgrammeCohortId(currentProgrammeCohortId ?? '')
    }
  }, [isOpen, currentProgrammeId, currentProgrammeCohortId])

  const selectedProgramme = useMemo(
    () => programmesQuery.data?.find((programme) => programme.id === programmeId),
    [programmeId, programmesQuery.data],
  )

  const availableCohorts: ProgrammeCohortModel[] = useMemo(
    () => selectedProgramme?.cohorts ?? [],
    [selectedProgramme],
  )

  useEffect(() => {
    if (!programmeCohortId) {
      return
    }

    if (availableCohorts.length === 0) {
      return
    }

    if (!availableCohorts.some((cohort) => cohort.id === programmeCohortId)) {
      setProgrammeCohortId('')
    }
  }, [availableCohorts, programmeCohortId])

  const mutation = useMutation({
    mutationFn: (payload: { programmeId: string; programmeCohortId?: string }) =>
      admissionsService.updateApplicationProgramme(
        applicationId,
        payload.programmeId,
        payload.programmeCohortId,
      ),
    onSuccess: (res) => {
      toast.success(res.message || 'Programme updated')
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] })
      onClose()
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to update programme')
    },
  })

  const isSubmitting = mutation.isPending
  const isProgrammeListLoading = programmesQuery.isLoading
  const loadError = programmesQuery.error as Error | undefined

  const handleSubmit = () => {
    const trimmedProgrammeId = programmeId.trim()
    const trimmedCohortId = programmeCohortId.trim()

    if (!trimmedProgrammeId) {
      toast.error('Please select a programme')
      return
    }

    mutation.mutate({
      programmeId: trimmedProgrammeId,
      programmeCohortId: trimmedCohortId ? trimmedCohortId : undefined,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Programme" size="sm">
      <div className="space-y-4">
        {isProgrammeListLoading && (
          <p className="text-sm text-gray-500">Loading programmes…</p>
        )}

        {loadError && (
          <p className="text-sm text-red-600">
            {loadError.message || 'Unable to load programmes'}
          </p>
        )}

        {!isProgrammeListLoading && !loadError && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Programme
              </label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40]"
                value={programmeId}
                onChange={(event) => {
                  const nextProgrammeId = event.target.value
                  setProgrammeId(nextProgrammeId)
                  setProgrammeCohortId('')
                }}
              >
                <option value="">Select programme</option>
                {programmesQuery.data?.map((programme) => (
                  <option key={programme.id} value={programme.id}>
                    {programme.title}
                  </option>
                ))}
              </select>
            </div>

            {availableCohorts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cohort (optional)
                </label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40]"
                  value={programmeCohortId}
                  onChange={(event) => setProgrammeCohortId(event.target.value)}
                >
                  <option value="">Select cohort</option>
                  {availableCohorts.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.title || 'Untitled cohort'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || isProgrammeListLoading}
          className="w-full bg-[#7b2a40] text-white py-2 rounded-md text-sm font-medium hover:bg-[#682133] transition disabled:opacity-70"
        >
          {isSubmitting ? 'Updating…' : 'Update Programme'}
        </button>
      </div>
    </Modal>
  )
}
